/**
 * Ask Flint — AI chat surface anchored at `/ask/$chatId`.
 *
 * The chat id lives in the URL so every conversation has a shareable
 * link from the first keystroke. Switching threads in the sidebar
 * navigates here; visiting `/ask` redirects to a freshly minted id.
 *
 * @see prototype/home/ask.html — V1 Ask Flint baseline.
 * @see 08-system-decisions.md §11 — AI narrates; deterministic code computes.
 * @see ../../../features/ai-chat — Studio-shaped component split.
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import { ChatShell } from "../../../features/ai-chat/index.ts";
import { meQueryOptions } from "../../../lib/session.ts";

export const Route = createFileRoute("/(workspace)/ask/$chatId")({
  // Same guard as `/ask` — without the Flint AI add-on the chat surface is
  // unreachable by URL, not just hidden from the nav (migration 0107).
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    if (me?.company && !me.company.modules?.includes("ai.assistant")) {
      throw redirect({ to: "/" });
    }
  },
  component: AskChatRoute,
});

function AskChatRoute() {
  const { chatId } = Route.useParams();
  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Ask Flint" }]}
      // Chat owns scroll for its sidebar list + transcript, so the
      // workspace ScrollArea would fight us. `disableViewportScroll`
      // swaps that for a plain `flex-1 min-h-0 overflow-hidden` shell.
      disableViewportScroll
      contentClassName="mx-0 flex h-full w-full max-w-none"
    >
      <ChatShell chatId={chatId} />
    </WorkspaceShell>
  );
}
